import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import {
  Ruler, Plus, X, ArrowLeft, CalendarClock, FileWarning, Wrench,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import ChipEstado from '../ui/ChipEstado';
import {
  VIGENCIA_INSTRUMENTO, ESTADO_INSTRUMENTO, RESULTADO_CALIBRACION,
} from '../ui/estados';
import {
  ESTADOS_INSTRUMENTO_EDITABLES, RESULTADOS_CALIBRACION, fechaLarga,
} from '../ui/calidad';
import { useAuth } from '../lib/contextoAuth';
import { Cargando } from '../ui/EstadoDePantalla';

// INSTRUMENTOS DE MEDICIÓN · Fase 5 del módulo de Calidad.
//
// QUÉ CONTESTA ESTA PANTALLA. La Fase 3 dejó guardado qué se midió y
// contra qué. Un auditor mira eso treinta segundos y pregunta lo
// siguiente: «¿y con qué lo midieron? Enséñeme el certificado de ese
// vernier». Aquí está esa respuesta, y la de después: «el vernier salió
// mal en marzo, ¿qué midieron con él desde entonces?».
//
// CUATRO DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **La vigencia va en la lista, ya calculada y con color.** Es lo
//      único que se viene a mirar aquí. Una lista de códigos y marcas
//      obligaría a abrir uno por uno para saber cuál hay que mandar al
//      laboratorio.
//   2. **Dar de alta pide dos campos.** El número grabado y qué es. La
//      marca, el modelo y la serie están en la etiqueta del aparato y se
//      copian con calma; exigirlos al alta es como acaban a medias las
//      hojas de Excel que esto viene a sustituir.
//   3. **Un instrumento recién dado de alta DICE que todavía no sirve.**
//      Sin calibración registrada no se puede medir con él, y descubrir
//      eso en el andén con la pieza en la mano es tarde.
//   4. **El análisis de impacto se ofrece siempre**, no solo cuando la
//      calibración sale mal. Pero cuando sale mal, se pone delante sin
//      que nadie lo busque: es el día en que hace falta.

const hoy = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------------
// Alta de un instrumento
// ---------------------------------------------------------------------
function FormularioAlta({ onCreado, onCancelar }) {
  const [v, setV] = useState({
    codigo: '', descripcion: '', tipo: '', marca: '', modelo: '', serie: '',
    rango: '', resolucion: '', ubicacion: '', frecuencia_meses: 12,
  });
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const set = (campo) => (e) => setV((a) => ({ ...a, [campo]: e.target.value }));

  const meses = Number(v.frecuencia_meses);
  const falta = !v.codigo.trim() ? 'el número que lleva grabado'
    : v.descripcion.trim().length < 2 ? 'qué es'
    : !(meses >= 1 && meses <= 120) ? 'cada cuántos meses se calibra (de 1 a 120)'
    : null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const cuerpo = { codigo: v.codigo, descripcion: v.descripcion,
                       frecuencia_meses: meses };
      ['tipo', 'marca', 'modelo', 'serie', 'rango', 'resolucion', 'ubicacion']
        .forEach((k) => { if (v[k].trim()) cuerpo[k] = v[k].trim(); });
      onCreado(await api.post('/instrumentos', cuerpo));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-4 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Dar de alta un instrumento</h3>
        <button type="button" onClick={onCancelar}
                className="text-gray-500 hover:text-gray-700" aria-label="Cancelar">
          <X size={18} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">
            Número del instrumento
          </span>
          <input value={v.codigo} onChange={set('codigo')} placeholder="VER-001"
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base" />
          <span className="block text-xs text-gray-500 mt-1">
            El que lleva grabado o en su etiqueta. Es como lo va a pedir un
            auditor.
          </span>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Qué es</span>
          <input value={v.descripcion} onChange={set('descripcion')}
                 placeholder="Vernier digital 0–150 mm"
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base" />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">
          Cada cuántos meses se calibra
        </span>
        <input type="number" min={1} max={120} value={v.frecuencia_meses}
               onChange={set('frecuencia_meses')}
               className="mt-1 w-full sm:w-48 border border-gray-300 rounded-lg px-3 py-2 text-base" />
        <span className="block text-xs text-gray-500 mt-1">
          Con esto TEGO calcula hasta cuándo vale cada calibración y te avisa
          antes de que se pase. Doce meses es lo normal; se puede cambiar
          después.
        </span>
      </label>

      {/* LO DE LA ETIQUETA VA JUNTO Y AL FINAL, y todo es opcional: se
          copia del aparato con calma, no con la persona esperando. */}
      <details className="border border-gray-200 rounded-xl p-3 bg-gray-50">
        <summary className="text-sm font-medium text-gray-700 cursor-pointer">
          Lo que dice la etiqueta del aparato (opcional)
        </summary>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {[
            ['tipo', 'Tipo', 'Vernier'],
            ['marca', 'Marca', 'Mitutoyo'],
            ['modelo', 'Modelo', '500-196-30'],
            ['serie', 'Número de serie', '12345678'],
            ['rango', 'Rango', '0 a 150 mm'],
            ['resolucion', 'Resolución', '0.01 mm'],
            ['ubicacion', 'Dónde vive', 'ALM-01'],
          ].map(([campo, etiqueta, ejemplo]) => (
            <label key={campo} className="block">
              <span className="block text-sm font-medium text-gray-700">{etiqueta}</span>
              <input value={v[campo]} onChange={set(campo)} placeholder={ejemplo}
                     className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          «Dónde vive» es el código de una ubicación del almacén, si quieres
          poder encontrarlo. Si la escribes tiene que existir ya.
        </p>
      </details>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!!falta || guardando}
                className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Dar de alta'}
        </button>
        {falta && <span className="text-sm text-gray-500">Falta {falta}.</span>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// Registrar una calibración
// ---------------------------------------------------------------------
function FormularioCalibracion({ instrumento, onListo, onCancelar }) {
  const [fecha, setFecha] = useState(hoy);
  const [resultado, setResultado] = useState('CONFORME');
  const [laboratorio, setLaboratorio] = useState('');
  const [numero, setNumero] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // VALIDACIÓN EN VIVO, no al guardar. La fecha del futuro la rechaza
  // también el servidor; decirlo aquí evita que alguien teclee el año
  // que viene, guarde, y se lleve el regaño después.
  const falta = !fecha ? 'la fecha del certificado' : null;
  const delFuturo = !!fecha && fecha > hoy();

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      // El archivo primero: si falla, no queda una calibración sin papel.
      let idArchivo = null;
      if (archivo) {
        const subido = await api.subir(
          '/calidad/adjuntos?proposito=CERTIFICADO_CALIBRACION', archivo);
        idArchivo = subido.id;
      }
      const cuerpo = { fecha, resultado };
      if (laboratorio.trim()) cuerpo.laboratorio = laboratorio.trim();
      if (numero.trim()) cuerpo.numero_certificado = numero.trim();
      if (idArchivo) cuerpo.id_archivo = idArchivo;
      onListo(await api.post(
        `/instrumentos/${instrumento.id}/calibraciones`, cuerpo));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <h4 className="font-semibold text-gray-900">Registrar una calibración</h4>
      <p className="text-sm text-gray-600">
        Se apunta la que <strong>ya se hizo</strong>, con la fecha del
        certificado. TEGO calcula solo hasta cuándo vale:{' '}
        {instrumento.frecuencia_meses} meses después.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">
            Fecha del certificado
          </span>
          <input type="date" value={fecha} max={hoy()}
                 onChange={(e) => setFecha(e.target.value)}
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">Cómo salió</span>
          <select value={resultado} onChange={(e) => setResultado(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
            {RESULTADOS_CALIBRACION.map(([val, txt]) => (
              <option key={val} value={val}>{txt}</option>
            ))}
          </select>
        </label>
      </div>

      {resultado === 'NO_CONFORME' && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Al guardar, TEGO te va a enseñar <strong>qué se midió con este
          instrumento</strong> desde la última calibración que sí pasó. Esa
          lista está en duda: decide tú qué hay que volver a revisar.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">
            Quién lo calibró
          </span>
          <input value={laboratorio} onChange={(e) => setLaboratorio(e.target.value)}
                 placeholder="Laboratorio Metrológico del Norte"
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700">
            Número del certificado
          </span>
          <input value={numero} onChange={(e) => setNumero(e.target.value)}
                 placeholder="CAL-2026-0912"
                 className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </label>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">
          El certificado (PDF o foto)
        </span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
               onChange={(e) => setArchivo(e.target.files?.[0] || null)}
               className="mt-1 w-full text-sm" />
        <span className="block text-xs text-gray-500 mt-1">
          Opcional, pero es el papel que va a pedir un auditor. Si no lo tienes
          ahora, registra la calibración igual y súbelo después con otra.
        </span>
      </label>

      {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!!falta || delFuturo || guardando}
                className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Registrar la calibración'}
        </button>
        <button type="button" onClick={onCancelar}
                className="text-sm text-gray-600 hover:underline">
          Cancelar
        </button>
        {delFuturo && (
          <span className="text-sm text-gray-500">
            Esa fecha es futura.
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Una calibración registrada no se puede modificar ni borrar: es la
        evidencia de que ese día el instrumento medía bien. Si te equivocas,
        registra otra — se conservan las dos y se ve que hubo una corrección.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------
// Qué se midió con él
// ---------------------------------------------------------------------
function Impacto({ datos }) {
  return (
    <section className="border border-amber-200 bg-amber-50 rounded-xl p-4 space-y-3">
      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
        <FileWarning size={18} className="text-amber-600" />
        Qué se midió con este instrumento
      </h4>
      <p className="text-sm text-gray-800">{datos.nota}</p>
      <p className="text-sm text-gray-800">
        {datos.desde
          ? <>Desde la calibración del <strong>{fechaLarga(datos.desde)}</strong>, que sí pasó.</>
          : <>Desde siempre: no hay ninguna calibración que haya pasado.</>}
        {' '}
        {(datos.inspecciones || []).length === 0
          ? 'No se ha medido nada con él en ese periodo.'
          : `${datos.mediciones} medida(s) en ${(datos.inspecciones || []).length} inspección(es), de las cuales ${datos.firmadas} ya está(n) firmada(s).`}
      </p>
      {(datos.inspecciones || []).length > 0 && (
        <div className="space-y-1">
          {(datos.inspecciones || []).map((i) => (
            <div key={i.id} className="bg-white border border-amber-200 rounded-lg p-2 text-sm">
              <span className="font-semibold text-gray-900">{i.folio}</span>
              {' · '}{i.sku} · lote {i.codigo_lote}
              {' · '}{i.mediciones} medida(s)
              {i.estado === 'FIRMADA' && (
                <span className="ml-2 text-xs font-semibold text-amber-800">Firmada</span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------
// La ficha de un instrumento
// ---------------------------------------------------------------------
function FichaInstrumento({ id, onVolver, onCambio }) {
  const { puede } = useAuth();
  const [ins, setIns] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [calibrando, setCalibrando] = useState(false);
  const [impacto, setImpacto] = useState(null);
  const [editando, setEditando] = useState(false);
  const [estado, setEstado] = useState('ACTIVO');
  const [frecuencia, setFrecuencia] = useState('');

  const cargar = useCallback(() => {
    api.get(`/instrumentos/${id}`)
      .then((d) => {
        setIns(d);
        setEstado(d.estado);
        setFrecuencia(String(d.frecuencia_meses));
      })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, [id]);

  useEffect(cargar, [cargar]);

  async function verImpacto() {
    try {
      setImpacto(await api.get(`/instrumentos/${id}/impacto`));
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.');
    }
  }

  async function guardarFicha(e) {
    e.preventDefault();
    setError(null);
    try {
      const cuerpo = {};
      if (estado !== ins.estado) cuerpo.estado = estado;
      if (Number(frecuencia) !== ins.frecuencia_meses) {
        cuerpo.frecuencia_meses = Number(frecuencia);
      }
      const d = await api.put(`/instrumentos/${id}`, cuerpo);
      setEditando(false);
      setAviso(d.aviso || `${d.codigo} quedó como «${d.estado_nombre.toLowerCase()}».`);
      cargar();
      onCambio?.();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    }
  }

  if (error && !ins) {
    return (
      <div className="space-y-4">
        <button onClick={onVolver} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline">
          <ArrowLeft size={16} /> Volver a los instrumentos
        </button>
        <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>
      </div>
    );
  }
  if (!ins) return <Cargando filas={4} />;

  const sinCambios = estado === ins.estado
    && Number(frecuencia) === ins.frecuencia_meses;

  return (
    <div className="space-y-5">
      <button onClick={onVolver} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline">
        <ArrowLeft size={16} /> Volver a los instrumentos
      </button>

      <header className="space-y-2">
        <h1 className="text-xl font-bold text-gray-900">
          {ins.codigo} · {ins.descripcion}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <ChipEstado mapa={VIGENCIA_INSTRUMENTO} clave={ins.vigencia} />
          <ChipEstado mapa={ESTADO_INSTRUMENTO} clave={ins.estado} />
        </div>
        <p className="text-sm text-gray-600">
          {ins.vigencia === 'SIN_CALIBRAR'
            ? 'Todavía no se puede medir con él: no tiene ninguna calibración registrada.'
            : ins.vigencia === 'VENCIDO'
              ? `Su calibración venció el ${fechaLarga(ins.proxima_fecha)}. Mándalo al laboratorio antes de volver a medir con él.`
              : `Calibrado el ${fechaLarga(ins.ultima_calibracion)}. Vale hasta el ${fechaLarga(ins.proxima_fecha)}.`}
        </p>
      </header>

      {aviso && (
        <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      <section className="border border-gray-200 rounded-xl p-4 bg-white space-y-1 text-sm text-gray-700">
        <div><strong>Se calibra cada</strong> {ins.frecuencia_meses} mes(es)</div>
        {ins.tipo && <div><strong>Tipo</strong> {ins.tipo}</div>}
        {(ins.marca || ins.modelo) && (
          <div><strong>Aparato</strong> {[ins.marca, ins.modelo].filter(Boolean).join(' ')}</div>
        )}
        {ins.serie && <div><strong>Número de serie</strong> {ins.serie}</div>}
        {ins.rango && <div><strong>Rango</strong> {ins.rango}</div>}
        {ins.resolucion && <div><strong>Resolución</strong> {ins.resolucion}</div>}
        {ins.ubicacion && <div><strong>Dónde vive</strong> {ins.ubicacion}</div>}
        {ins.responsable && <div><strong>Responsable</strong> {ins.responsable}</div>}
        <div>
          <strong>Se ha usado en</strong> {ins.mediciones} medida(s) de inspección
        </div>
      </section>

      {puede('calidad.instrumentos') && (
        editando ? (
          <form onSubmit={guardarFicha}
                className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
            <h4 className="font-semibold text-gray-900">Cambiar el instrumento</h4>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">Cómo está</span>
              <select value={estado} onChange={(e) => setEstado(e.target.value)}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {ESTADOS_INSTRUMENTO_EDITABLES.map(([val, txt]) => (
                  <option key={val} value={val}>{txt}</option>
                ))}
              </select>
              <span className="block text-xs text-gray-500 mt-1">
                Fuera de servicio y de baja no se pueden usar para medir. Nada
                se borra: las medidas que ya se tomaron con él se conservan.
              </span>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700">
                Cada cuántos meses se calibra
              </span>
              <input type="number" min={1} max={120} value={frecuencia}
                     onChange={(e) => setFrecuencia(e.target.value)}
                     className="mt-1 w-full sm:w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <span className="block text-xs text-gray-500 mt-1">
                Vale a partir de la siguiente calibración. Las que ya están
                registradas conservan su fecha de vencimiento.
              </span>
            </label>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={sinCambios}
                      className="bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
                Guardar
              </button>
              <button type="button" onClick={() => setEditando(false)}
                      className="text-sm text-gray-600 hover:underline">Cancelar</button>
              {sinCambios && (
                <span className="text-xs text-gray-500">No has cambiado nada todavía.</span>
              )}
            </div>
          </form>
        ) : (
          <button onClick={() => { setEditando(true); setAviso(null); }}
                  className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold">
            <Wrench size={16} /> Cambiar el instrumento
          </button>
        )
      )}

      {/* LAS CALIBRACIONES */}
      <section className="space-y-2">
        <h2 className="font-semibold text-gray-900">Sus calibraciones</h2>

        {puede('calidad.instrumentos') && (
          calibrando ? (
            <FormularioCalibracion
              instrumento={ins}
              onCancelar={() => setCalibrando(false)}
              onListo={(d) => {
                setCalibrando(false);
                setAviso(d.mensaje);
                if (d.impacto) setImpacto(d.impacto);
                cargar();
                onCambio?.();
              }}
            />
          ) : (
            <button onClick={() => { setCalibrando(true); setAviso(null); }}
                    className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Registrar una calibración
            </button>
          )
        )}

        {(ins.calibraciones || []).length === 0 ? (
          <EstadoVacio
            icono={CalendarClock}
            titulo="Este instrumento nunca se ha calibrado aquí"
            descripcion={
              'Mientras no haya una calibración registrada, TEGO no deja medir '
              + 'con él: no hay forma de demostrar que mide bien. Si ya lo '
              + 'calibraste, apunta la última con la fecha de su certificado.'
            }
          />
        ) : (
          <div className="space-y-2">
            {(ins.calibraciones || []).map((c) => (
              <div key={c.id} className="border border-gray-200 rounded-xl p-3 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">
                      {fechaLarga(c.fecha)}
                    </div>
                    <div className="text-sm text-gray-600">
                      Valía hasta el {fechaLarga(c.proxima_fecha)}
                      {c.laboratorio ? ` · ${c.laboratorio}` : ''}
                      {c.numero_certificado ? ` · certificado ${c.numero_certificado}` : ''}
                    </div>
                    <div className="text-sm text-gray-600">
                      {c.registrado_por
                        ? `Lo apuntó ${c.registrado_por}.`
                        : 'Lo apuntó alguien que ya no está en el sistema.'}
                      {!c.id_archivo && ' Sin el certificado adjunto.'}
                    </div>
                  </div>
                  <ChipEstado mapa={RESULTADO_CALIBRACION} clave={c.resultado} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* EL ANÁLISIS DE IMPACTO. Se ofrece siempre y no solo cuando algo
          sale mal: la pregunta «¿qué se midió con esto?» también se hace
          el día que se pierde un instrumento o se cae al suelo. */}
      {impacto ? <Impacto datos={impacto} /> : (
        <button onClick={verImpacto}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-marca)] hover:underline">
          <FileWarning size={16} /> Ver qué se midió con este instrumento
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// La pantalla
// ---------------------------------------------------------------------
export default function Instrumentos() {
  const { puede } = useAuth();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [alta, setAlta] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [abierto, setAbierto] = useState(null);
  useSubpantalla('instrumento', !!abierto, () => setAbierto(null), { valor: abierto, abrir: setAbierto });
  const [verBaja, setVerBaja] = useState(false);

  const cargar = useCallback(() => {
    api.get(`/instrumentos${verBaja ? '?incluir_baja=true' : ''}`)
      .then(setDatos)
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, [verBaja]);

  useEffect(cargar, [cargar]);

  if (abierto) {
    return (
      <FichaInstrumento
        id={abierto}
        onVolver={() => { setAbierto(null); cargar(); }}
        onCambio={cargar}
      />
    );
  }

  const lista = datos?.instrumentos || [];
  const pendientes = (datos?.vencidos || 0) + (datos?.sin_calibrar || 0);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Instrumentos de medición</h1>
        <p className="text-sm text-gray-600">
          Con qué se mide en esta planta, y hasta cuándo vale la calibración de
          cada uno. Es lo que pide un auditor después de ver una inspección.
        </p>
      </header>

      {aviso && (
        <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {pendientes > 0 && (
        <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
          <strong>
            {pendientes} instrumento(s) no se pueden usar para medir ahora mismo
          </strong>{' '}
          — {datos.vencidos} con la calibración vencida y {datos.sin_calibrar} sin
          ninguna calibración registrada. Están marcados en rojo abajo.
          <Ayuda id="instrumentos.calibracion_requerida" />
        </div>
      )}

      {alta ? (
        <FormularioAlta
          onCancelar={() => setAlta(false)}
          onCreado={(d) => { setAlta(false); setAviso(d.mensaje); cargar(); }}
        />
      ) : puede('calidad.instrumentos') && (
        <button onClick={() => { setAlta(true); setAviso(null); }}
                className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold">
          <Plus size={16} /> Dar de alta un instrumento
        </button>
      )}

      {datos === null && <Cargando filas={4} />}

      {datos !== null && lista.length === 0 && !alta && (
        <EstadoVacio
          icono={Ruler}
          titulo="Todavía no hay ningún instrumento dado de alta"
          descripcion={
            'Un instrumento es el vernier, la báscula o el micrómetro con el '
            + 'que se mide. Darlos de alta con su calibración es lo que permite '
            + 'contestar «¿con qué midieron esto, y estaba calibrado?» — que es '
            + 'la segunda pregunta de toda auditoría. Mientras no haya ninguno, '
            + 'quien inspecciona sigue escribiendo el instrumento a mano, como '
            + 'hasta ahora.'
          }
          accion={puede('calidad.instrumentos') && (
            <button onClick={() => setAlta(true)}
                    className="inline-flex items-center gap-2 bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Dar de alta el primero
            </button>
          )}
        />
      )}

      {datos !== null && lista.length > 0 && (
        <>

          <div className="space-y-2">
            {lista.map((i) => (
              <button key={i.id} onClick={() => setAbierto(i.id)}
                      className="w-full text-left border border-gray-200 rounded-xl p-3 bg-white hover:border-gray-300">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">
                      {i.codigo} · {i.descripcion}
                    </div>
                    <div className="text-sm text-gray-600">
                      {i.proxima_fecha
                        ? `Su calibración vale hasta el ${fechaLarga(i.proxima_fecha)}`
                        : 'Sin ninguna calibración registrada'}
                      {i.ubicacion ? ` · en ${i.ubicacion}` : ''}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <ChipEstado mapa={VIGENCIA_INSTRUMENTO} clave={i.vigencia} />
                    {i.estado !== 'ACTIVO' && (
                      <ChipEstado mapa={ESTADO_INSTRUMENTO} clave={i.estado} />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <button onClick={() => setVerBaja((v) => !v)}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:underline">
            {verBaja ? 'Ver solo los que siguen existiendo' : 'Ver también los dados de baja'}
          </button>
        </>
      )}
    </div>
  );
}
